import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { DownloadIcon } from "../../../../icons";
import { VisibilityIcon } from "../../../../icons";
import { PDFViewerDialog } from "../../../../common/components/PDFViewerDialog";
import { isOriginalCrudStatus, RowCrudStatus } from "../../../common/types";
import { UserAttachmentEditionUpdateStore } from "../../store";
import { InputString } from "../../../common/controls";
import { FileCRUDType } from "../../../common/types";
import { GridChangeSet } from "../../../common/types";
import { LoadFilesCRUDCompact } from "../../../common/controls";
import { InputText } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type UserAttachmentEditionUpdateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  name: string;
  nameSelectedValue?: { displayId: string; id: string } | null;
  onNameChange?: (value: string) => void;
  nameError?: string;
  fileOnRestoreFile: (id: string) => void;
  fileOnLoadFile: (file: any) => void;
  fileOnCommitFile: (changes: GridChangeSet) => void;
  fileFiles: FileCRUDType[];
  fileError?: string;
  notes: string;
  notesSelectedValue?: { displayId: string; id: string } | null;
  onNotesChange?: (value: string) => void;
  notesError?: string;
  dummyCRUDRestoredObservable: number;
  userAttachmentEditionUpdateStore: UserAttachmentEditionUpdateStore;
};

export const UserAttachmentEditionUpdateForm: React.FC<UserAttachmentEditionUpdateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      name = "",
      nameError,
      onNameChange = () => {},
      fileOnRestoreFile,
      fileOnLoadFile,
      fileOnCommitFile,
      fileFiles,
      fileError,
      notes = "",
      notesError,
      onNotesChange = () => {},
      dummyCRUDRestoredObservable,
      userAttachmentEditionUpdateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      const getViewAction = (
        onDownload: (props: {
          id: string;
          type: string;
          name: string;
          __status__: RowCrudStatus;
        }) => void,
        onViewPdf?: (props: any) => void
      ) => {
        const actions: any[] = [];
        if (onViewPdf) {
          actions.push({
            columnName: "__action_view_pdf",
            onClick: (props: any) =>
              isOriginalCrudStatus(props.__status__) ? onViewPdf(props) : {},
            icon: <VisibilityIcon />,
            label: langStore.viewCommand,
            isActionEnabled: (props: any) =>
              props.name?.toLowerCase().endsWith(".pdf"),
          });
        }
        actions.push({
          columnName: "__action_view",
          onClick: (props: any) =>
            isOriginalCrudStatus(props.__status__) ? onDownload(props) : {},
          icon: <DownloadIcon />,
          label: langStore.downloadCommand,
        });
        return actions;
      };
      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelUpdatedEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="UserAttachmentEdition"
              mode="update"
              values={{}}
              options={{}}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "name":
                    onNameChange?.(valor);
                    break;
                  case "notes":
                    onNotesChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={userAttachmentEditionUpdateStore.getCardErrors("null")}
              title={langStore.groupUserAttachmentEditionLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputString
                  autoFocus
                  value={name}
                  error={Boolean(nameError)}
                  warning={
                    !nameError &&
                    Boolean(userAttachmentEditionUpdateStore.warnings["name"])
                  }
                  helperText={
                    nameError ||
                    userAttachmentEditionUpdateStore.warnings["name"]?.message
                  }
                  label={langStore.UserAttachmentEditionNameLabel}
                  onChange={(value: string) => onNameChange(value)}
                  description={langStore.UserAttachmentEditionNameDescription}
                />
                <LoadFilesCRUDCompact
                  rows={fileFiles}
                  maxRows={1}
                  acceptFileTypes=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg,.gif"
                  menuActions={getViewAction(
                    (props) =>
                      userAttachmentEditionUpdateStore.fileDownload(
                        props,
                        "file"
                      ),
                    (props) =>
                      userAttachmentEditionUpdateStore.viewFilePdf(
                        props,
                        "file"
                      )
                  )}
                  showAddCommand={true}
                  showDeleteCommand={true}
                  label={langStore.UserAttachmentEditionFileLabel}
                  description={langStore.UserAttachmentEditionFileDescription}
                  onLoadFile={(file: File) => fileOnLoadFile(file)}
                  columns={[
                    { name: "mimetype", title: langStore.fileTypeLabel },
                    { name: "name", title: langStore.fileNameLabel },
                    { name: "formattedSize", title: langStore.fileSizeLabel },
                  ]}
                  onRestore={(id: string) => fileOnRestoreFile(id)}
                  onCommitChanges={(changes: GridChangeSet) =>
                    fileOnCommitFile(changes)
                  }
                  dummyCRUDRestoredObservable={dummyCRUDRestoredObservable}
                  error={fileError}
                />
                <InputText
                  value={notes}
                  error={Boolean(notesError)}
                  warning={
                    !notesError &&
                    Boolean(userAttachmentEditionUpdateStore.warnings["notes"])
                  }
                  helperText={
                    notesError ||
                    userAttachmentEditionUpdateStore.warnings["notes"]?.message
                  }
                  label={langStore.UserAttachmentEditionNotesLabel}
                  onChange={(value: string) => onNotesChange(value)}
                  nullText={langStore.optionalFieldHint}
                  description={langStore.UserAttachmentEditionNotesDescription}
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
          <PDFViewerDialog
            open={userAttachmentEditionUpdateStore.pdfViewerOpen}
            onClose={() => userAttachmentEditionUpdateStore.closePdfViewer()}
            pdfUrl={userAttachmentEditionUpdateStore.pdfViewerUrl}
            title={userAttachmentEditionUpdateStore.pdfViewerTitle}
          />
        </Grid>
      );
    }
  );
